import React, { memo } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';

import * as L from '../../_detailShared/detail.styled';
import { GroupColors } from './groupColors';

export type RosterFilter = 'all' | 'active' | 'terminated' | 'debtors';

interface IStudentRosterFiltersProps {
  filter: RosterFilter;
  setFilter: (f: RosterFilter) => void;
  query: string;
  setQuery: (q: string) => void;
  total: number;
  filtered: number;
  onAddStudent: () => void;
}

const FILTERS: RosterFilter[] = ['all', 'active', 'terminated', 'debtors'];

const Bar = styled.div`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px;
  margin: 12px 0 8px;
`;

const Chip = styled.button<{ active?: boolean }>`
  border: 1px solid
    ${({ active }) => (active ? GroupColors.accent : GroupColors.border)};
  background: ${({ active }) =>
    active ? GroupColors.accentSurface : GroupColors.surface};
  color: ${({ active }) =>
    active ? GroupColors.accentStrong : GroupColors.textSecondary};
  font-size: 12px;
  font-weight: 600;
  padding: 4px 10px;
  border-radius: 999px;
  cursor: pointer;
  &:hover {
    background: ${({ active }) =>
      active ? GroupColors.accentSurface : GroupColors.surfaceSubtle};
  }
`;

const SearchInput = styled.input`
  flex: 1;
  min-width: 200px;
  max-width: 320px;
  height: 30px;
  border: 1px solid ${GroupColors.border};
  border-radius: 6px;
  padding: 0 10px;
  font-size: 12px;
  background: ${GroupColors.surface};
  outline: none;
  &:focus {
    border-color: ${GroupColors.accent};
  }
`;

const Count = styled.span`
  font-size: 11px;
  color: ${GroupColors.textMuted};
  font-weight: 600;
  margin-left: auto;
`;

/**
 * Roster header strip for the GroupDetail Students tab — filter chips
 * (`Все / Активные / Завершённые / Должники`) + client-side search by
 * name/email + a primary "+ Студент" action. Counters help the
 * operator confirm the filter did the right thing.
 */
export const StudentRosterFilters = memo(
  ({
    filter,
    setFilter,
    query,
    setQuery,
    total,
    filtered,
    onAddStudent,
  }: IStudentRosterFiltersProps) => {
    const { t } = useTranslation();
    return (
      <Bar>
        {FILTERS.map((f) => (
          <Chip
            key={f}
            type="button"
            active={filter === f}
            onClick={() => setFilter(f)}
          >
            {t(`pages.groupDetail.students.filter.${f}`)}
          </Chip>
        ))}
        <SearchInput
          type="search"
          value={query}
          placeholder={t('pages.groupDetail.students.searchPlaceholder')}
          onChange={(e) => setQuery(e.target.value)}
        />
        <Count>
          {t('pages.groupDetail.students.count', { filtered, total })}
        </Count>
        <L.PrimaryActionButton
          type="button"
          tone="primary"
          onClick={onAddStudent}
        >
          {t('pages.groupDetail.actions.addStudent')}
        </L.PrimaryActionButton>
      </Bar>
    );
  },
);
